/* ============================================================================
   Steffie's Fit Finder — engine (v4, Jen + Steffie's revised sizing guide,
   "SteffiesClaude_FitFinderSizingGuide_FINAL_2026-07-01_r2").

   Sizing runs off Jen's Landmark matrix (TRUE SIZE + WIDTH + FOOT VOLUME +
   ANKLE); Explorer and Glide are derived from it. The universal rule:
   Steffie's runs ½ small, so everyone sizes up at least ½ EXCEPT a true-narrow
   (AA/AAA/AAAA) foot, who is true to size.

   v4.1 (2026-09-22): Journey gained an elastic back and now fits true to size
   for every foot INCLUDING true-narrow; only a Wide (D+) foot sizes up (+½, or
   +1 with volume). Glide now runs 5–12 and Explorer 5–12 (variants live; the
   server-rendered stock map picks the new sizes up with no engine change).

   Structure: the PURE ENGINE below is DOM-free and exported (window.SteffieFitEngine
   + module.exports) so it can be unit-tested in Node against the doc's 26 worked
   cases. The DOM wiring lives under the `typeof document` guard at the bottom.
   ========================================================================== */
(function(){
  "use strict";

  /* ---- live stock + image + title map, server-rendered (window.STEFFIE_FIT_STOCK).
     Settable so Node tests can inject a stub. ---- */
  var STOCK = {};

  /* --- Families. `wide` lists the colorways that come in a true Wide width
     (only Space & Safari). --- */
  var FAMILIES = {
    landmark: { name:"Landmark", colorways:["space","safari","palace","lava","landmark-pearl","landmark-petal","landmark-ivy"], wide:["space","safari"] },
    journey:  { name:"Journey",  colorways:["journey-sand"], wide:[] },
    glide:    { name:"Glide",    colorways:["glide","glide-ink"], wide:[] },
    explorer: { name:"Explorer", colorways:["explorer-space"], wide:[] }
  };
  // If a family is sold out in her size, the next families to try (stock switching).
  var FALLBACK = {
    landmark: ["journey","glide","explorer"],
    journey:  ["explorer","landmark","glide"],
    glide:    ["journey","explorer","landmark"],
    explorer: ["landmark","journey","glide"]
  };

  /* --- Q3 Width → internal code. We collect plain words (Jen: only ~25% know
     their A–E letter). v4 splits narrow into True narrow (AA+) and Slightly
     narrow (A); Extra-wide is gone (E folds into Wide/D+). --- */
  var WIDTH_OPTS = [
    { label:"True narrow",     desc:"Quite slim through the ball. Most shoes feel roomy, and straps or heels tend to slip.", code:"AA" },
    { label:"Slightly narrow", desc:"A touch narrow. Shoes are usually okay but can feel a little loose.",                  code:"A"  },
    { label:"Standard",        desc:"The everyday width most women's shoes are built for.",                                  code:"B"  },
    { label:"Slightly wide",   desc:"A little extra room across the ball.",                                                  code:"C"  },
    { label:"Wide",            desc:"Noticeably more room across the ball and a deeper toe box.",                            code:"D"  },
    { label:"Not sure",        desc:"That's fine. We'll read it from your other answers.",                                  code:"?"  }
  ];
  /* --- Q4 Foot volume → "Shoe Fit Experience" set (Jen-confirmed). `vol` collapses
     to the matrix's yes/no volume flag: low/standard = no volume, full/swell = volume. --- */
  var VOLUME_OPTS = [
    { label:"Preventing slippage",      desc:"My feet are on the leaner side, and straps or heels often slip.",           vol:"low"      },
    { label:"Standard proportion",      desc:"Standard sizes generally fit comfortably all day.",                      vol:"standard" },
    { label:"Generous room baseline",   desc:"I naturally need a fuller, more accommodating fit from the start.",      vol:"full"     },
    { label:"Late-day pressure relief", desc:"My feet tend to swell, so I need extra room by afternoon.",                vol:"swell"    }
  ];
  /* --- Q5 Ankle → only affects Landmark (the Wide strap). Strap-fit language (Jen). --- */
  var ANKLE_OPTS = [
    { label:"Straps usually have room to spare", ank:"thin"    },
    { label:"Straps fit just right",             ank:"regular" },
    { label:"Straps can feel snug at the ankle", ank:"thick"   }
  ];

  /* --- Landmark matrix (Jen's, exact — v4 three-column form). Each foot bucket ×
     ankle → size-up in half-steps from true size, and whether it's a Wide width.
     Regular ankle = "either": mirrors the thick (Wide) cell, and landmarkFit also
     surfaces the thin-column regular-width size as a co-equal alternative. --- */
  var LANDMARK = {
    //                thin (regular)        thick (Wide)          regular ("either" → Wide + regular alt)
    AA:   { thin:{up:0,   wide:false}, thick:{up:0,   wide:false}, regular:{up:0,   wide:false} },
    Blow: { thin:{up:0.5, wide:false}, thick:{up:0.5, wide:false}, regular:{up:0.5, wide:false} }, // A (all vols) · B low
    Bvol: { thin:{up:1,   wide:false}, thick:{up:0.5, wide:true },  regular:{up:0.5, wide:true } },
    Cno:  { thin:{up:1,   wide:false}, thick:{up:0.5, wide:true },  regular:{up:0.5, wide:true } },
    Cvol: { thin:{up:1.5, wide:false}, thick:{up:1,   wide:true },  regular:{up:1,   wide:true } },
    Dno:  { thin:{up:1.5, wide:false}, thick:{up:1,   wide:true },  regular:{up:1,   wide:true } },
    Dvol: { thin:{up:2,   wide:false}, thick:{up:1.5, wide:true },  regular:{up:1.5, wide:true } }
  };
  function footBucket(code, hasVol){
    if (code === '?') code = 'B';        // not sure → treat as standard
    if (code === 'AA') return 'AA';      // true narrow → true to size
    if (code === 'A')  return 'Blow';    // slightly narrow → +½ at ALL volumes (the +½ row)
    if (code === 'B')  return hasVol ? 'Bvol' : 'Blow';
    if (code === 'C')  return hasVol ? 'Cvol' : 'Cno';
    if (code === 'D')  return hasVol ? 'Dvol' : 'Dno';
    if (code === 'E')  return 'Dvol';    // safety: E no longer offered, maps to widest bucket
    return hasVol ? 'Bvol' : 'Blow';
  }

  function half(n){ return Math.round(n * 2) / 2; }      // snap to nearest half size
  function widthRank(code){ return ['AA','A','B','C','D','E'].indexOf(code); }

  /* --- Per-family sizing. Returns {recommended:false, reason} (not the right
     family for her foot) or {recommended:true, attempts:[{size,wide,...}]}. --- */
  function landmarkFit(R, code, hasVol, ankle){
    var b = footBucket(code, hasVol);
    var row = LANDMARK[b];
    var cell = row[ankle] || row.regular;
    var attempts = [];
    if (cell.wide){
      // thick ankle → Wide is required (route to any wide colorway, e.g. Space/Safari).
      // regular ankle → "either": prefer Wide within this occasion's own wide styles,
      // and advertise the regular-width size as a co-equal alternative.
      var scope = (ankle === 'thick') ? 'any' : 'pref';
      var wide = { size:half(R + cell.up), wide:true, wideScope:scope, note:'Wide' };
      if (ankle === 'regular') wide.altSize = half(R + row.thin.up);   // "· or size X regular"
      attempts.push(wide);
      attempts.push({ size:half(R + row.thin.up), wide:false, note:'regular width' });
    } else {
      attempts.push({ size:half(R + cell.up), wide:false, note:(code === 'AA' ? 'true to size' : 'runs narrow') });
    }
    return { recommended:true, attempts:attempts };
  }
  // Journey (flat, v4.1 elastic back) = true to size for everyone, true-narrow
  // included. Only a Wide (D+) foot moves: +½ without volume, +1 with volume.
  // Never uses Wide. (Steffie, 2026-09-22.)
  function journeyFit(R, code, hasVol){
    var w = (code === '?') ? 'B' : code;
    var up = 0, note = 'true to size';
    if (w === 'D' || w === 'E'){ up = hasVol ? 1 : 0.5; note = 'sized up for a wide foot'; }
    return { recommended:true, attempts:[{ size:half(R + up), wide:false, note:note }] };
  }
  // Explorer (boot) = Landmark all-regular column as written, ankle ignored (socks
  // cancel the extra room). Fits a true-narrow foot true to size.
  function explorerFit(R, code, hasVol){
    var up = LANDMARK[footBucket(code, hasVol)].thin.up;
    return { recommended:true, attempts:[{ size:half(R + up), wide:false, note:'true to size with socks' }] };
  }
  // Glide (sandal) = true to length, strap runs wide, full sizes only. Never for a
  // true-narrow foot. Slightly-narrow (A) follows the Standard (B) rows.
  function glideFit(R, code, hasVol){
    var w = (code === '?') ? 'B' : code;
    if (w === 'AA') return { recommended:false, reason:'narrow' };   // true narrow → not recommended
    if (w === 'A')  w = 'B';                                          // slightly narrow follows Standard rows
    var isHalf = (Math.round(R * 2) % 2) !== 0;
    var size, note;
    if (!isHalf){
      size = R; note = 'true to size';                               // whole size: B-low / B-vol / C / D+ all → true
    } else if (w === 'B' && !hasVol){
      size = R - 0.5; note = 'sized for a closer fit';               // half + standard low volume → down ½
    } else {
      size = R + 0.5; note = 'sized up';                             // half + (B-with-volume or C+) → up ½
    }
    return { recommended:true, attempts:[{ size:half(size), wide:false, note:note }] };
  }
  function sizingFor(famKey, R, prof){
    if (famKey === 'landmark') return landmarkFit(R, prof.width, prof.hasVol, prof.ankle);
    if (famKey === 'journey')  return journeyFit(R, prof.width, prof.hasVol);
    if (famKey === 'explorer') return explorerFit(R, prof.width, prof.hasVol);
    if (famKey === 'glide')    return glideFit(R, prof.width, prof.hasVol);
    return { recommended:false, reason:'unknown' };
  }

  /* --- Season (client-side; Jen's grid pairs spring/summer vs fall/winter). --- */
  function isWarm(now){ var mo = (now || new Date()).getMonth(); return mo >= 2 && mo <= 7; } // Mar–Aug

  /* --- Q1 occasion → ranked colorway picks, season-aware (Jen's confirmed grid). --- */
  function occasionPicks(occasion, warm){
    switch (occasion){
      case 0: return [ { fam:'landmark', colorways:['space','safari','landmark-ivy'] } ];                                 // Work
      case 1: return [ { fam:'landmark', colorways:['palace','landmark-pearl', warm ? 'landmark-petal' : 'lava'] } ];     // Weddings/nights
      case 2: return [ { fam:'journey', colorways:['journey-sand'] },                                                     // Everyday errands
                       warm ? { fam:'glide', colorways:['glide','glide-ink'] } : { fam:'explorer', colorways:['explorer-space'] } ];
      case 3: return [ { fam:'journey', colorways:['journey-sand'] },                                                     // Transitional / all-day
                       warm ? { fam:'landmark', colorways:['safari','space'] } : { fam:'explorer', colorways:['explorer-space'] } ];
      default: return [ { fam:'landmark', colorways:['space','safari','landmark-ivy'] } ];
    }
  }
  // Ankle only matters for Landmark → only ask it when this occasion/season can yield a Landmark pick.
  // (Landmark recs require the Q5 ankle answer — Jen.)
  function needsAnkle(occasion, warm){ return occasion === 0 || occasion === 1 || (occasion === 3 && warm); }

  // Wide (D+) feet can't be accommodated by Palace/Pearl (regular-width only) → route
  // them to Space/Safari (our only wide-capable Landmark styles), even at Weddings.
  function landmarkColorways(cfg, prof){
    if (cfg.fam !== 'landmark' || prof.width !== 'D') return cfg.colorways;
    for (var i = 0; i < cfg.colorways.length; i++){
      if (FAMILIES.landmark.wide.indexOf(cfg.colorways[i]) > -1) return cfg.colorways; // already wide-capable (e.g. Work)
    }
    return ['safari','space'];
  }

  // Size → Shopify taxonomy id (kept for a possible future per-style size link; currently unused).
  var SIZE_TAXONOMY = { "5":"2941","5.5":"2942","6":"17101","6.5":"2943","7":"2944","7.5":"2945","8":"17102","8.5":"2946","9":"2947","9.5":"2948","10":"17103","10.5":"3010","11":"3011","11.5":"3012","12":"17210" };

  /* --- Result-screen copy. [COPY] = placeholder wording for Ann Marie/Steffie. --- */
  var COPY = {
    whyWide:     "We've pointed you to the widths with real room across the ball, sized so nothing pinches by the end of the day.",
    whyVolume:   "RoamFoam™ gives the front of your foot room to breathe, and we've accounted for feet that fill out as the day goes on.",
    whyNarrow:   "Landmark runs narrow, and the quick-release clasp locks your heel in place so it stays put without your toes gripping forward to keep it on.",
    whyNarrowJourney: "Journey's elastic back was made for a foot like yours: it holds a narrow heel in place, so we've kept you true to size with no slipping and no need to size down.",
    whyStandard: "We've sized you for all-day comfort, with RoamFoam™ cushioning underfoot and an engineered heel that keeps your weight balanced.",
    flatFeet:    "Many of our customers with flat feet tell us these are the most comfortable shoes they own.",
    highArch:    "And we've accounted for your high arches in your fit, too.",
    bunion:      "We've factored your bunions into your fit, with a little extra room across the ball.",
    // §6: Landmark is the toughest of our styles for bunions — flag it honestly, lean Wide, point to return insurance.
    bunionLandmark: "One honest note: of all our styles, the Landmark wedge is the trickiest for bunions, so we've sized you into our Wide for the most room across the ball. If it's not quite right, add return insurance at checkout and your return ships free. You can also email help@steffies.com and we'll talk it through.",
    // Fit-consult nudge when a true-narrow foot is steered away from Glide.
    narrowNudge: "Because your feet run true-narrow, we've steered you away from the styles that would run large on you. Want a second opinion? Email help@steffies.com and we'll walk through your fit together.",
    // §6 teaser (no product) for the widest feet / very high arches in Landmark. [COPY] Ann Marie.
    teaserHead:  "We see you, and we're building a shoe for you.",
    teaserBody:  "For the widest feet and very high arches, we'd rather be honest than sell you the wrong shoe: our current Landmark last isn't quite there for you yet. You're exactly who we're designing for right now, and a roomier, almond-toe fit is on the way. Leave us a note at help@steffies.com and we'll tell you the moment it lands.",
    rule: {
      landmark: "Landmark runs narrow, and your recommendation already accounts for that.",
      journey:  "Journey fits true to size, and its elastic back holds your heel snug. Your recommendation already accounts for that.",
      glide:    "Glide fits true to length, and your recommendation dials in the width for you.",
      explorer: "Explorer runs short and is made to wear with socks, and your recommendation accounts for both."
    }
  };

  function nameOf(handle){ return (STOCK[handle] && STOCK[handle].title) || handle; }
  function imageOf(handle){ return (STOCK[handle] && STOCK[handle].image) || ''; }
  function inStock(handle, key){ return !!(STOCK[handle] && STOCK[handle].sizes && STOCK[handle].sizes[key] === true); }
  function parseSize(str){ if (!str) return null; var m = (''+str).match(/[0-9]{1,2}(?:[.]5)?/); return m ? parseFloat(m[0]) : null; }
  function fmtSize(n){ return String(n); }

  /* Free-text Q6 → structured flags. Bunions & extra-wide bump the width; "widest"
     (EE+/triple-E/"widest") and "very high arches" trigger the §6 Landmark teaser;
     plain high arches / flat feet get a reassuring note only. */
  function parseExtras(text){
    var t = (text || '').toLowerCase();
    return {
      bunions:      /bunion/.test(t),
      extraWide:    /extra[\s-]*wide/.test(t),
      widest:       /widest|triple\s*e|\bee+\b|\b[3-9]\s*e\b|\beee+\b/.test(t),
      flatFeet:     /flat\s*feet|flat-?foot|fallen\s*arch|low\s*arch/.test(t),
      highArch:     /high\s*arch|high-arch|high\s*instep/.test(t),
      veryHighArch: /very\s*high\s*arch|very\s*high\s*instep|extreme(?:ly)?\s*high\s*arch|super\s*high\s*arch/.test(t)
    };
  }

  /* Resolve an in-stock colorway within one family. Returns a pick, OR null
     (family suits her but is sold out in her size), OR {recommended:false, reason}
     (family isn't the right fit for her foot). */
  function resolveInFamily(famKey, colorwaysPref, R, prof){
    var fam = FAMILIES[famKey];
    var sizing = sizingFor(famKey, R, prof);
    if (!sizing.recommended) return { recommended:false, reason:sizing.reason };

    if (R == null){ // no reference size → can't stock-check; show preferred colorway descriptively
      var c0 = (colorwaysPref && colorwaysPref[0]) || fam.colorways[0];
      var a0 = sizing.attempts[0];
      return { famKey:famKey, handle:c0, name:nameOf(c0), display:"We'll set your size on the next page", link:null, note:a0.note, wide:a0.wide, switched:false, stockKnown:false };
    }
    for (var a = 0; a < sizing.attempts.length; a++){
      var at = sizing.attempts[a];
      var pool;
      if (at.wide){
        // "pref" scope (regular ankle) only uses this occasion's own wide styles; "any" (thick ankle) may route to Space/Safari.
        pool = (at.wideScope === 'pref' && colorwaysPref)
             ? colorwaysPref.filter(function(c){ return fam.wide.indexOf(c) > -1; })
             : fam.wide.slice();
      } else {
        pool = colorwaysPref ? colorwaysPref.slice() : fam.colorways.slice();
      }
      var cands = pool.filter(function(c){ return fam.colorways.indexOf(c) > -1; });
      if (!cands.length) continue;
      var key = at.wide ? (fmtSize(at.size) + " Wide") : fmtSize(at.size);
      var prefer = cands[0];
      for (var i = 0; i < cands.length; i++){
        if (inStock(cands[i], key)){
          return { famKey:famKey, handle:cands[i], name:nameOf(cands[i]),
                   display:"Size " + fmtSize(at.size) + (at.wide ? ", Wide" : ""),
                   link:key, note:at.note, wide:at.wide, altSize:(at.altSize != null ? at.altSize : null),
                   switched:(cands[i] !== prefer || a > 0), stockKnown:true };
        }
      }
    }
    return null; // suits her, but sold out in her size across every attempt
  }

  /* Last-resort recommendation: the occasion's lead style at her best size, shown
     even if we can't confirm stock. Used only for non-narrow, non-teaser stock-outs. */
  function forcePick(cfg, R, prof){
    var effCw = landmarkColorways(cfg, prof);
    var fam = FAMILIES[cfg.fam], prefer = effCw[0];
    var sizing = sizingFor(cfg.fam, R, prof);
    if (!sizing.recommended) return null;
    var at = sizing.attempts.filter(function(a){ return !a.wide; })[0] || sizing.attempts[0];
    var colorway = (at.wide && fam.wide.length) ? fam.wide[0] : prefer;
    var key = (R == null) ? null : (at.wide ? (fmtSize(at.size) + " Wide") : fmtSize(at.size));
    return { famKey:cfg.fam, handle:colorway, name:nameOf(colorway),
             display:(R == null ? "Your size is set on the next page" : ("Size " + fmtSize(at.size) + (at.wide ? ", Wide" : ""))),
             link:key, note:at.note, wide:at.wide, altSize:(at.altSize != null ? at.altSize : null), switched:false, stockKnown:false, preferName:nameOf(prefer), preferHandle:prefer };
  }

  /* Resolve a configured pick: try its family (with wide-foot routing), then other
     families in her size, else show the lead. */
  function resolvePick(cfg, R, prof){
    var effCw = landmarkColorways(cfg, prof);
    var preferName = nameOf(effCw[0]);
    var primary = resolveInFamily(cfg.fam, effCw, R, prof);
    if (primary && primary.recommended !== false){ primary.preferName = preferName; primary.preferHandle = effCw[0]; return primary; }
    var order = FALLBACK[cfg.fam] || [];
    for (var i = 0; i < order.length; i++){
      var alt = resolveInFamily(order[i], null, R, prof);
      if (alt && alt.recommended !== false){ alt.switchedFamily = true; alt.preferName = preferName; alt.preferHandle = effCw[0]; return alt; }
    }
    return forcePick(cfg, R, prof);  // nothing confirmed in stock → still recommend the lead style
  }

  /* Build the recommendation set from the answers. */
  function computeResult(occasion, R, prof, now){
    var warm = isWarm(now);
    var picksCfg = occasionPicks(occasion, warm);
    var teaser = !!(prof.widest || prof.veryHighArch);   // §6: widest feet / very high arches → not our best fit in Landmark
    var narrowNudge = false;
    var seen = {}, picks = [];
    picksCfg.forEach(function(cfg){
      if (teaser && (cfg.fam === 'landmark' || cfg.fam === 'journey')) return;   // §6: don't force a size in Landmark; Journey also excludes the widest feet
      if (prof.width === 'AA' && cfg.fam === 'glide'){ narrowNudge = true; return; } // Glide is not recommended for true-narrow (Journey is, since v4.1)
      var p = resolvePick(cfg, R, prof);
      if (!p || seen[p.handle]) return;
      seen[p.handle] = true; picks.push(p);
    });
    // true-narrow with only Glide options left for this occasion → substitute Explorer (true to size on a narrow foot).
    if (!picks.length && !teaser && prof.width === 'AA'){
      var ep = resolvePick({ fam:'explorer', colorways:['explorer-space'] }, R, prof);
      if (ep && !seen[ep.handle]){ seen[ep.handle] = true; picks.push(ep); }
    }
    var teaserOnly = teaser && !picks.length;   // nothing but Landmark/Journey for this occasion → full teaser screen
    if (!picks.length && !teaser){ var f = forcePick(picksCfg[0], R, prof); if (f) picks.push(f); } // never a silent dead-end
    return { picks:picks, ref:R, prof:prof, warm:warm, teaserOnly:teaserOnly, narrowNudge:narrowNudge };
  }

  /* Reassurance "why" line, built from her profile + the primary pick. */
  function buildWhy(prof, picks){
    var lead = picks[0] || {};
    var why;
    if (prof.bunions && lead.famKey === 'landmark') why = COPY.bunionLandmark;      // §6 honest bunion note (replaces)
    else if (prof.wideFoot || prof.bunions)         why = COPY.whyWide;
    else if (prof.hasVol)                            why = COPY.whyVolume;
    else if (prof.width === 'AA')                    why = (lead.famKey === 'journey') ? COPY.whyNarrowJourney : COPY.whyNarrow;
    else                                            why = COPY.whyStandard;
    if (prof.flatFeet)                                    why += " " + COPY.flatFeet;
    if (prof.bunions && lead.famKey !== 'landmark')       why += " " + COPY.bunion;
    if (prof.highArch)                                    why += " " + COPY.highArch;
    return why;
  }

  /* ---- Export the pure engine (Node tests + any future callers). ---- */
  var Engine = {
    setStock: function(s){ STOCK = s || {}; },
    computeResult: computeResult,
    buildWhy: buildWhy,
    parseExtras: parseExtras,
    parseSize: parseSize,
    isWarm: isWarm,
    needsAnkle: needsAnkle,
    widthRank: widthRank,
    WIDTH_OPTS: WIDTH_OPTS, VOLUME_OPTS: VOLUME_OPTS, ANKLE_OPTS: ANKLE_OPTS,
    _internals: { footBucket:footBucket, landmarkFit:landmarkFit, journeyFit:journeyFit, explorerFit:explorerFit, glideFit:glideFit, occasionPicks:occasionPicks, landmarkColorways:landmarkColorways, LANDMARK:LANDMARK }
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = Engine;
  if (typeof window !== 'undefined') window.SteffieFitEngine = Engine;

  /* ========================================================================
     DOM WIRING (browser only). Everything below runs against the live markup.
     ======================================================================== */
  if (typeof document === 'undefined') return;
  var root = document.getElementById('steffie-fit');
  if (!root) return;

  try { STOCK = window.STEFFIE_FIT_STOCK || {}; } catch(e){ STOCK = {}; }

  // Logged-in customers: a saved Fit Profile is rendered server-side by Liquid.
  // Mirror it into localStorage so the size pre-selects on this device, and greet her.
  try {
    var w = document.querySelector('.steffie-fit__welcome');
    if (w && w.getAttribute('data-fit-saved')) {
      var sv = JSON.parse(w.getAttribute('data-fit-saved'));
      if (sv && sv.handle) {
        localStorage.setItem('steffie_fit_profile', JSON.stringify(sv));
        if (sv.picks) { var mm = JSON.parse(localStorage.getItem('steffie_fit_sizes')||'{}'); sv.picks.forEach(function(p){ if(p.size) mm[p.handle]=p.size; }); localStorage.setItem('steffie_fit_sizes', JSON.stringify(mm)); }
        else if (sv.size) { var m2 = JSON.parse(localStorage.getItem('steffie_fit_sizes')||'{}'); m2[sv.handle]=sv.size; localStorage.setItem('steffie_fit_sizes', JSON.stringify(m2)); }
        w.innerHTML = 'Welcome back. Your fit is <strong>'+(sv.style||'')+'</strong>'+(sv.size?(', size '+sv.size):'')+'.';
      }
    }
  } catch(e){}

  var answers = { occasion:null, ref:"", refSize:null, width:null, volume:null, ankle:null, extras:"" };
  var stepHistory = [];   // steps she's actually seen, so "Back" retraces correctly (incl. the skipped ankle step)

  function currentStep(){ var on = root.querySelector('.steffie-fitstep.on'); return on ? parseInt(on.getAttribute('data-step'), 10) : null; }

  function show(step){
    root.querySelectorAll('.steffie-fitstep').forEach(function(s){ s.classList.remove('on'); });
    var el = root.querySelector('.steffie-fitstep[data-step="'+step+'"]');
    if (el) el.classList.add('on');
    markSelected();   // reflect any answer she already gave on this step
  }
  function go(step){ var cur = currentStep(); if (cur != null) stepHistory.push(cur); show(step); }   // forward, remembering where we were
  function back(){ var prev = stepHistory.pop(); if (prev != null) show(prev); }                       // return to the previous step she saw

  // Highlight the option matching her current answer (so going back shows her pick).
  function markSelected(){
    root.querySelectorAll('.steffie-fitopts button').forEach(function(b){
      b.classList.toggle('is-selected', answers[b.dataset.q] != null && parseInt(b.dataset.val, 10) === answers[b.dataset.q]);
    });
  }

  // Tappable answer buttons (occasion / width / volume / ankle).
  root.querySelectorAll('.steffie-fitopts button').forEach(function(b){
    b.addEventListener('click', function(){
      var q = b.dataset.q;
      answers[q] = parseInt(b.dataset.val, 10);
      var step = parseInt(b.closest('.steffie-fitstep').getAttribute('data-step'), 10);
      // After volume (step 4): skip the ankle step (5) when ankle can't matter.
      if (q === 'volume' && !needsAnkle(answers.occasion, isWarm())) { go(6); return; }
      go(step + 1);
    });
  });

  // Back links (present on every step after the first).
  root.querySelectorAll('[data-fit-back]').forEach(function(a){
    a.addEventListener('click', function(e){ e.preventDefault(); back(); });
  });

  // Question 2 = a size grid (2026-10-08). The size is required, so the result always
  // names an exact size; Continue stays disabled until she taps one.
  var refBtns = root.querySelectorAll('[data-ref-size]');
  var refNext = root.querySelector('.steffie-fit-continue');
  var refNeed = root.querySelector('.steffie-fitneed');
  function paintRef(){
    refBtns.forEach(function(b){ var on = b.getAttribute('data-ref-size') === answers.refSize; b.classList.toggle('is-selected', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    if (refNext) refNext.disabled = !answers.refSize;
    if (refNeed) refNeed.textContent = answers.refSize ? '' : 'Choose a size to continue.';
  }
  refBtns.forEach(function(b){ b.addEventListener('click', function(){ answers.refSize = b.getAttribute('data-ref-size'); paintRef(); }); });

  // Pressing Enter inside a text box advances, just like clicking Continue / See my fit.
  var refInput = document.getElementById('steffie-fit-ref');
  if (refInput) refInput.addEventListener('keydown', function(e){ if (e.key === 'Enter') { e.preventDefault(); if (answers.refSize) go(3); } });
  var extrasInput = document.getElementById('steffie-fit-extras');
  if (extrasInput) extrasInput.addEventListener('keydown', function(e){ if (e.key === 'Enter') { e.preventDefault(); finish(); } });

  function enc(v){ return encodeURIComponent(v); }

  /* Paint the result screen from a computed result. */
  function render(result){
    var lineEl = document.getElementById('steffie-fit-line');
    var whyEl  = document.getElementById('steffie-fit-why');
    var ruleEl = document.getElementById('steffie-fit-rule');
    var ctaEl  = document.getElementById('steffie-fit-cta');
    var othersEl = document.getElementById('steffie-fit-others');
    var insEl  = document.getElementById('steffie-fit-insurance');

    // §6 teaser: the widest feet / very high arches in a Landmark-only occasion — no forced size.
    if (result.teaserOnly){
      lineEl.textContent = COPY.teaserHead;   // big heading style (#steffie-fit-line is .steffie-fitrec__size)
      whyEl.textContent = COPY.teaserBody;
      if (ruleEl) ruleEl.textContent = '';
      if (ctaEl){
        ctaEl.innerHTML = '';
        var mail = document.createElement('a');
        mail.className = 'button steffie-fit-cta__btn';
        mail.textContent = 'Tell Us About Your Fit →';
        mail.href = 'mailto:help@steffies.com?subject=' + enc('My fit — the widest feet / very high arches');
        ctaEl.appendChild(mail);
      }
      if (insEl) insEl.textContent = '';
      if (othersEl) othersEl.innerHTML = 'Curious to look around? <a class="steffie-tlink" href="/collections/all">Browse the full collection →</a>';
      show(7);
      return;
    }

    var picks = result.picks;
    lineEl.textContent = picks.length > 1 ? "Here are two styles in your size." : "Here is your match.";

    var why = buildWhy(result.prof || {}, picks);
    if (result.narrowNudge) why += " " + COPY.narrowNudge;   // fit-consult nudge for a true-narrow foot
    whyEl.textContent = why;

    if (ruleEl){
      if (picks.length > 1){
        ruleEl.textContent = "Sizes can differ by style, so each size above is set for that shoe.";
      } else if (picks.length){
        ruleEl.textContent = COPY.rule[picks[0].famKey] || "";
      } else {
        ruleEl.textContent = "";
      }
    }

    // Result = a buy box per pick (2026-10-08): on-foot photo, live stars, price, her
    // recommended size preselected among the other sizes, and Add to cart into the drawer.
    if (ctaEl){
      ctaEl.innerHTML = '';
      picks.forEach(function(p, i){ ctaEl.appendChild(buyBox(p, { trust: i === 0 })); });
      var alt = picks.length === 1 ? altPick(result) : null;   // a second style in her size, labelled as the alternative
      if (alt) ctaEl.appendChild(buyBox(alt, { alt: alt.label }));
    }

    if (insEl) insEl.textContent = (root.getAttribute('data-insurance-note') || '');

    // Back-in-stock capture: her preferred colorway is sold out in her size (so we
    // switched her, or could not confirm stock). Offer one email when it lands.
    renderBis(picks);

    // "Not your style?\" — send her to the full collection (no size filter: sizing differs by family).
    if (othersEl){
      othersEl.innerHTML = 'Not quite your style? <a class="steffie-tlink" href="/collections/all">Browse the full collection →</a>';
    }

    show(7);
  }

  function esc(v){ return String(v == null ? '' : v).replace(/[&<>"']/g, function(c){ return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]; }); }

  /* One result buy box (v2, 2026-10-10, Ross: "do the Fit Finder result screen next").
     Her size as a big badge, colour swatches for families with more than one colorway (swap in
     place, her recommended size kept; colours sold out in her size are greyed), a stronger Add to
     Cart, See Full Details as a real secondary button, and an optional trust row. Size pills list
     this style's sizes in the same width as her recommendation; sold-out sizes stay visible but
     disabled. The recommendation itself (handle, size, wide) comes from the engine unchanged. */
  var TRUST_HTML = '<ul class="steffie-fitbuy__trust"><li>Free U.S. shipping</li><li>30-day returns</li><li>Designed by a neuroscientist</li></ul>';
  function colorName(handle){ var t = nameOf(handle); return t.split(' ').slice(1).join(' ') || t; }
  function swatchList(p){
    var fam = FAMILIES[p.famKey]; if (!fam) return [];
    var pool = (p.wide && fam.wide && fam.wide.length) ? fam.wide : fam.colorways;
    return pool.filter(function(h){ return !!STOCK[h]; });
  }
  function buyBox(p, opts){
    opts = opts || {};
    var wideKeys = /\sWide$/;
    var handle = p.handle, st = STOCK[handle] || {}, chosen = null;
    var sizeLabel = (p.link != null) ? String(p.link).replace(wideKeys, '') + (p.wide ? ' Wide' : '') : null;

    var box = document.createElement('div');
    box.className = 'steffie-fitbuy' + (opts.alt ? ' steffie-fitbuy--alt' : '');
    var colors = swatchList(p);
    box.innerHTML =
        (opts.alt ? '<div class="steffie-fitbuy__tag">' + esc(opts.alt) + '</div>' : '')
      + '<a class="steffie-fitbuy__media" data-fb-link><img data-fb-img alt="" width="360" height="450" loading="lazy"></a>'
      + '<div class="steffie-fitbuy__body">'
      +   (sizeLabel ? '<div class="steffie-fitbuy__badge"><span class="steffie-fitbuy__badgelabel">Your size</span><span class="steffie-fitbuy__badgesize">' + esc(sizeLabel) + '</span></div>' : '')
      +   '<a class="steffie-fitbuy__name" data-fb-link data-fb-name></a>'
      +   '<div class="steffie-fitbuy__meta" data-fb-meta></div>'
      +   '<p class="steffie-fitbuy__rec" data-fb-rec></p>'
      +   (colors.length > 1 ? '<div class="steffie-fitbuy__colors"><div class="steffie-fitbuy__colorlabel">Color: <span data-fb-color></span></div><div class="steffie-fitbuy__swatches" role="group" aria-label="Colors">'
            + colors.map(function(h){
                var sw = (STOCK[h] && STOCK[h].swatch) || '';
                var ok = p.link == null || (STOCK[h].sizes && STOCK[h].sizes[p.link] === true);
                return '<button type="button" class="steffie-fitbuy__sw' + (ok ? '' : ' is-out') + '" data-h="' + esc(h) + '"'
                     + ' style="' + (sw ? 'background:' + esc(sw) + ';background-size:cover;' : '') + '"'
                     + ' aria-label="' + esc(colorName(h)) + (ok ? '' : ', sold out in size ' + esc(sizeLabel)) + '" title="' + esc(colorName(h)) + (ok ? '' : ' (sold out in your size)') + '"></button>';
              }).join('')
            + '</div></div>' : '')
      +   '<div class="steffie-fitbuy__sizes" role="group" aria-label="' + (p.wide ? 'Wide sizes' : 'Sizes') + '" data-fb-sizes></div>'
      +   '<button type="button" class="button steffie-fitbuy__add"></button>'
      +   '<a class="steffie-fitbuy__details" data-fb-link>See Full Details</a>'
      +   (opts.trust ? TRUST_HTML : '')
      +   '<div class="steffie-fitbuy__msg" aria-live="polite"></div>'
      + '</div>';

    var add = box.querySelector('.steffie-fitbuy__add'), msg = box.querySelector('.steffie-fitbuy__msg');
    function paintAdd(){
      add.disabled = !chosen;
      add.textContent = chosen ? ('Add to Cart · Size ' + chosen.replace(wideKeys, '') + (p.wide ? ' Wide' : '')) : 'Choose Your Size';
      box.querySelectorAll('.steffie-fitbuy__size').forEach(function(b){
        var on = b.dataset.key === chosen; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    // Paint everything that depends on the colorway; called once, then on every swatch tap.
    function paint(h){
      handle = h; st = STOCK[h] || {};
      var href = '/products/' + h + (p.link != null ? '?fit_size=' + enc(p.link) : '');
      box.querySelectorAll('[data-fb-link]').forEach(function(a){ a.href = href; });
      var img = box.querySelector('[data-fb-img]');
      if (st.photo){ img.src = st.photo; img.alt = nameOf(h) + ' on foot'; img.hidden = false; } else { img.hidden = true; }
      box.querySelector('[data-fb-name]').textContent = nameOf(h);
      var stars = (st.rating && st.ratingCount >= 3)
        ? '<span class="steffie-fitbuy__stars"><span aria-hidden="true">★</span> ' + esc(st.rating) + ' <span class="steffie-fitbuy__count">(' + esc(st.ratingCount) + ' reviews)</span></span>' : '';
      box.querySelector('[data-fb-meta]').innerHTML = stars + (st.price ? '<span class="steffie-fitbuy__price">' + esc(st.price) + '</span>' : '');
      var colorEl = box.querySelector('[data-fb-color]'); if (colorEl) colorEl.textContent = colorName(h);
      box.querySelectorAll('.steffie-fitbuy__sw').forEach(function(b){ var on = b.dataset.h === h; b.classList.toggle('is-sel', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      var recOk = p.link != null && st.sizes && st.sizes[p.link] === true;
      // The recommendation sentence only when it adds something beyond the badge (no-size case, an alternate size, or a sold-out size).
      var rec = '';
      if (p.link == null) rec = 'Choose your size below.';
      if (p.altSize != null) rec += (rec ? ' ' : '') + 'Size ' + esc(fmtSize(p.altSize)) + ' in regular width also works for your foot.';
      if (p.link != null && !recOk) rec += (rec ? ' ' : '') + 'Your size is sold out in this color today. Try another color above.';
      var recEl = box.querySelector('[data-fb-rec]'); recEl.innerHTML = rec; recEl.hidden = !rec;
      var keys = Object.keys(st.sizes || {})
        .filter(function(k){ return p.wide ? wideKeys.test(k) : !wideKeys.test(k); })
        .sort(function(x, y){ return parseFloat(x) - parseFloat(y); });
      if (chosen && !(st.sizes && st.sizes[chosen] === true)) chosen = null;   // a size she tapped isn't available in this colour
      if (!chosen && recOk) chosen = p.link;                                      // her recommended size comes back whenever it's in stock
      box.querySelector('[data-fb-sizes]').innerHTML = keys.map(function(k){
        var avail = st.sizes[k] === true, label = k.replace(wideKeys, '');
        return '<button type="button" class="steffie-fitbuy__size' + (k === p.link ? ' is-rec' : '') + '"'
             + ' data-key="' + esc(k) + '" aria-pressed="false"'
             + (avail ? '' : ' disabled aria-label="Size ' + esc(label) + ', sold out"') + '>' + esc(label) + '</button>';
      }).join('');
      box.querySelectorAll('.steffie-fitbuy__size').forEach(function(b){
        b.addEventListener('click', function(){ chosen = b.dataset.key; msg.textContent = ''; paintAdd(); });
      });
      msg.textContent = '';
      paintAdd();
    }
    box.querySelectorAll('.steffie-fitbuy__sw').forEach(function(b){
      b.addEventListener('click', function(){ if (b.dataset.h !== handle) paint(b.dataset.h); });
    });
    add.addEventListener('click', function(){
      var vid = chosen && st.vids && st.vids[chosen];
      if (!vid) return;
      addToCart(vid, p.link != null ? p.link : chosen, add, msg);
    });
    paint(handle);
    return box;
  }

  /* A second style in her size when the engine returned only one (labelled as the alternative).
     Uses the engine's own resolvePick, so sizing and stock rules are identical; shown only when
     that style is confirmed in stock in her size and is a different family. */
  var ALT_FAMILIES = { landmark:['journey','explorer'], explorer:['journey','landmark'], glide:['journey','landmark'], journey:['landmark','explorer'] };
  var ALT_LABEL  = { journey:'Prefer a flat? Also in your size', landmark:'Prefer a heel? Also in your size', explorer:'Prefer a boot? Also in your size' };
  function altPick(result){
    var lead = result.picks[0]; if (!lead || result.ref == null) return null;
    var prof = result.prof || {};
    var fams = ALT_FAMILIES[lead.famKey] || [];
    for (var i = 0; i < fams.length; i++){
      var fam = fams[i];
      if (fam === lead.famKey) continue;
      if ((prof.widest || prof.veryHighArch) && (fam === 'landmark' || fam === 'journey')) continue;   // same §6 rule as computeResult
      if (prof.width === 'AA' && fam === 'glide') continue;
      var alt = resolveInFamily(fam, FAMILIES[fam].colorways, result.ref, prof);
      if (!alt || alt.recommended === false || !alt.stockKnown || alt.handle === lead.handle) continue;   // only a style confirmed in stock in her size
      alt.label = ALT_LABEL[fam] || 'Another style in your size';
      return alt;
    }
    return null;
  }

  /* Add one pair through the theme's cart drawer, carrying the same order tracking the
     PDP uses: the hidden _fit_finder line property (her recommended size) and the
     fit-finder cart attribute that the "Tag Fit Finder orders" Flow turns into a tag. */
  function addToCart(variantId, recSize, btn, msg){
    var drawer = document.querySelector('cart-drawer');
    var hasDrawer = drawer && typeof drawer.renderContents === 'function';
    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');
    fd.append('properties[_fit_finder]', String(recSize));
    if (hasDrawer){
      try {
        fd.append('sections', drawer.getSectionsToRender().map(function(s){ return s.id; }).join(','));
        fd.append('sections_url', window.location.pathname);
      } catch (err) {}
    }
    btn.setAttribute('aria-busy', 'true'); btn.disabled = true;
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(addUrl, { method:'POST', headers:{ 'Accept':'application/javascript', 'X-Requested-With':'XMLHttpRequest' }, body:fd })
      .then(function(r){ return r.json(); })
      .then(function(res){
        btn.removeAttribute('aria-busy'); btn.disabled = false;
        if (res && res.status){ msg.textContent = res.description || 'That size could not be added. Please try another.'; return; }
        fetch('/cart/update.js', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ attributes:{ 'fit-finder':'yes' } }) }).catch(function(){});
        if (!hasDrawer){ window.location.href = '/cart'; return; }
        drawer.classList.remove('is-empty');
        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(btn);
        drawer.renderContents(res);
      })
      .catch(function(){ btn.removeAttribute('aria-busy'); btn.disabled = false; msg.textContent = 'Something went wrong. Please try again.'; });
  }

  var BIS_RESTOCK = {};   // handle -> arrival phrase, e.g. { space: 'early October' }; empty since the Sep 22 core-size restock landed
  function renderBis(picks){
    var old = document.getElementById('steffie-fit-bis'); if (old) old.remove();
    var ctaEl = document.getElementById('steffie-fit-cta'); if (!ctaEl) return;
    var target = null;
    for (var i = 0; i < picks.length; i++){
      var p = picks[i];
      if (p.link == null || !p.preferHandle) continue;
      var pref = STOCK[p.preferHandle];
      if (!pref || !pref.sizes || pref.sizes[p.link] !== false) continue;   // preferred colorway is fine → nothing to capture
      if (!pref.vids || !pref.vids[p.link]) continue;
      target = { handle:p.preferHandle, name:nameOf(p.preferHandle), size:p.link, vid:pref.vids[p.link] };
      break;
    }
    if (!target) return;
    var when = BIS_RESTOCK[target.handle] ? ' The restock lands in ' + BIS_RESTOCK[target.handle] + '.' : '';
    var box = document.createElement('div');
    box.className = 'steffie-fitbis'; box.id = 'steffie-fit-bis';
    box.innerHTML = '<div class="steffie-fitbis__lead">Your size in <strong>' + target.name + '</strong> is sold out today.' + when + '</div>'
      + '<form class="steffie-fitbis__form" novalidate><input class="steffie-fitbis__input" type="email" name="email" placeholder="Your email" autocomplete="email" required>'
      + '<button type="submit" class="button steffie-fitbis__btn">One Email When Size ' + target.size + ' Lands</button></form>'
      + '<div class="steffie-fitbis__msg" aria-live="polite"></div>';
    ctaEl.parentNode.insertBefore(box, ctaEl.nextSibling);
    var form = box.querySelector('form'), input = box.querySelector('input'), msg = box.querySelector('.steffie-fitbis__msg'), btn = box.querySelector('button');
    form.addEventListener('submit', function(e){
      e.preventDefault();
      var email = (input.value || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)){ msg.textContent = 'That email does not look right yet.'; return; }
      var key = window.STEFFIE_FIT_KLAVIYO; if (!key){ msg.textContent = 'Something went wrong. Please try again.'; return; }
      btn.disabled = true;
      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(key), {
        method:'POST', headers:{ 'Content-Type':'application/json', 'revision':'2026-07-15' },
        body: JSON.stringify({ data:{ type:'back-in-stock-subscription',
          attributes:{ profile:{ data:{ type:'profile', attributes:{ email:email } } }, channels:['EMAIL'] },
          relationships:{ variant:{ data:{ type:'catalog-variant', id:'$shopify:::$default:::' + target.vid } } } } })
      }).then(function(r){
        if (r.ok || r.status === 202){ form.hidden = true; msg.textContent = 'Done. You will get one email when size ' + target.size + ' in ' + target.name + ' is back, and nothing else.'; }
        else { btn.disabled = false; msg.textContent = 'Something went wrong. Please try again.'; }
      }).catch(function(){ btn.disabled = false; msg.textContent = 'Something went wrong. Please try again.'; });
    });
  }

  function buildProfile(){
    // The tapped size leads the saved reference string, so parseSize() always reads it
    // (a brand like "Pegasus 40" can follow without being mistaken for the size).
    var refEl = document.getElementById('steffie-fit-ref');
    var brand = refEl ? refEl.value.trim() : "";
    answers.ref = answers.refSize ? (answers.refSize + (brand ? " · " + brand : "")) : brand;
    var exEl = document.getElementById('steffie-fit-extras');
    answers.extras = exEl ? exEl.value.trim() : "";

    var ref = parseSize(answers.ref);
    var widthCode = (answers.width != null && WIDTH_OPTS[answers.width]) ? WIDTH_OPTS[answers.width].code : '?';
    var volKey = (answers.volume != null && VOLUME_OPTS[answers.volume]) ? VOLUME_OPTS[answers.volume].vol : 'standard';
    var ankle = (answers.ankle != null && ANKLE_OPTS[answers.ankle]) ? ANKLE_OPTS[answers.ankle].ank : 'regular';
    var extras = parseExtras(answers.extras);

    // Bunions / a written-in "extra wide" ~ a wide width (Jen): bump width to at least D for sizing.
    if ((extras.bunions || extras.extraWide) && widthRank(widthCode) < widthRank('D')) widthCode = 'D';

    var prof = {
      width: widthCode,
      hasVol: (volKey === 'full' || volKey === 'swell'),
      ankle: ankle,
      wideFoot: (widthRank(widthCode) >= widthRank('C')),
      bunions: extras.bunions, highArch: extras.highArch, flatFeet: extras.flatFeet,
      widest: extras.widest, veryHighArch: extras.veryHighArch
    };

    var result = computeResult(answers.occasion, ref, prof);
    var primary = result.picks[0] || {};
    var profile = {
      occasion: answers.occasion, width: widthCode, volume: volKey, ankle: ankle, extras: answers.extras,
      style: primary.name, handle: primary.handle, rule: primary.note,
      size: (primary.link != null ? String(primary.link) : null),
      ref: answers.ref,
      picks: result.picks.map(function(p){ return { name:p.name, handle:p.handle, size:(p.link != null ? String(p.link) : null) }; }),
      ts: Date.now()
    };
    return { profile: profile, result: result };
  }

  function finish(e){
    if (e) e.preventDefault();
    var cur = currentStep(); if (cur != null && cur !== 7) stepHistory.push(cur);  // so "Back" from the result returns to the last question
    var built = buildProfile();
    render(built.result);
    var saved = document.getElementById('steffie-fit-saved');
    try {
      if (built.result.teaserOnly){
        // Nothing to pre-select — don't persist a size map, just clear any stale save line.
        if (saved) saved.textContent = "";
      } else {
        localStorage.setItem('steffie_fit_profile', JSON.stringify(built.profile));
        var map = JSON.parse(localStorage.getItem('steffie_fit_sizes') || '{}');
        built.profile.picks.forEach(function(p){ if (p.size) map[p.handle] = p.size; });
        localStorage.setItem('steffie_fit_sizes', JSON.stringify(map));
        if (saved) saved.textContent = "We saved this as your Fit Profile, so your size will be ready on every product page.";
      }
    } catch(err){ if (saved) saved.textContent = ""; }

    var sync = root.querySelector('[data-fit-sync]');
    if (sync && !built.result.teaserOnly && window.SteffieFitAccount && window.SteffieFitAccount.enabled){
      sync.style.display = '';
      sync.onclick = function(ev){ ev.preventDefault(); sync.textContent = 'Syncing…'; window.SteffieFitAccount.connect(built.profile); };
    } else if (sync){
      sync.style.display = 'none';
    }
    root.scrollIntoView({ behavior:'smooth', block:'start' });
  }

  root.querySelectorAll('[data-fit-finish]').forEach(function(a){ a.addEventListener('click', finish); });
  // "Continue" / "Skip" on the reference-shoe step advances to width (step 3).
  root.querySelectorAll('[data-fit-next]').forEach(function(a){
    a.addEventListener('click', function(e){ e.preventDefault(); go(parseInt(a.getAttribute('data-fit-next'), 10)); });
  });

  var restart = root.querySelector('[data-fit-restart]');
  if (restart) restart.addEventListener('click', function(e){
    e.preventDefault();
    answers = { occasion:null, ref:"", refSize:null, width:null, volume:null, ankle:null, extras:"" };
    stepHistory.length = 0;
    paintRef();
    var r = document.getElementById('steffie-fit-ref'); if (r) r.value = "";
    var x = document.getElementById('steffie-fit-extras'); if (x) x.value = "";
    show(1); root.scrollIntoView({ behavior:'smooth', block:'start' });
  });

  /* Returning from the account-sync redirect (?code&state): restore the result from the
     saved profile instead of dumping the visitor back at Question 1. */
  (function(){
    var p = new URLSearchParams(location.search);
    if (!p.get('code') || !p.get('state')) return;
    var saved = null; try { saved = JSON.parse(localStorage.getItem('steffie_fit_profile') || 'null'); } catch(e){}
    if (!saved) return;
    var ex = parseExtras(saved.extras || '');
    var widthCode = saved.width || '?';
    if ((ex.bunions || ex.extraWide) && widthRank(widthCode) < widthRank('D')) widthCode = 'D';
    var prof = {
      width: widthCode,
      hasVol: (saved.volume === 'full' || saved.volume === 'swell'),
      ankle: saved.ankle || 'regular',
      wideFoot: (widthRank(widthCode) >= widthRank('C')),
      bunions: ex.bunions, highArch: ex.highArch, flatFeet: ex.flatFeet, widest: ex.widest, veryHighArch: ex.veryHighArch
    };
    var savedRef = parseSize(saved.ref);
    if (savedRef == null) return;   // an old profile without a size: start the quiz fresh rather than show a sizeless result
    var result = computeResult(saved.occasion, savedRef, prof);
    render(result);
    var s = document.getElementById('steffie-fit-saved');
    if (s && !result.teaserOnly) s.textContent = "Syncing to your account…";
    root.scrollIntoView({ behavior:'auto', block:'start' });
  })();
})();
